import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import type {
  AdministrativeGender,
  GenderIdentity,
  ProfessionalRole,
} from "@curai/js-fhir-tools";

export enum UnsolicitedReason {
  PATIENT_MATCH_FAILED = "patient-match-failed",
  ORDER_MATCH_FAILED = "order-match-failed",
}

export interface TaskListResponse {
  taskListItems: (
    | ErroredRxTaskItem
    | LabResultsTaskItem
    | UnsolicitedLabTaskItem
    | LabFailedTaskItem
    | QuestionnaireResponseReviewTaskItem
  )[];
}

export interface TaskItem {
  taskDueAt: string;
  itemType: TaskListItemType;
}

export interface ErroredRxTaskItem extends TaskItem {
  patient: TaskListPatientData;
  prescriber: TaskListProviderData;
  prescription: TaskListPrescriptionData;
  failureAt: string;
}

export interface LabResultsTaskItem extends TaskItem {
  patient: TaskListPatientData;
  orderingProvider: TaskListProviderData;
  serviceRequestId: string;
  labTestName: string;
  labOrderedAt: string;
  reportIssuedAt?: string;
}
export interface UnsolicitedLabTaskItem extends TaskItem {
  patientName: string;
  curaiPatientId?: string;
  diagnosticReportId: string;
  reportIssuedAt?: string;
  pdfUrls?: string[];
  unsolicitedReason?: UnsolicitedReason;
}

export interface LabFailedTaskItem extends TaskItem {
  failureAt: string;
  labOrderedAt: string;
  patient: TaskListPatientData;
  orderingProvider: TaskListProviderData;
  requestGroupId: string;
  labTestName: string;
  reportIssuedAt?: string;
}

export interface LabNeedsSubmissionTaskItem extends TaskItem {
  patient: TaskListPatientData;
  orderingProvider: TaskListProviderData;
  requestGroupId: string;
  labOrderedAt: string;
}

export interface QuestionnaireResponseReviewTaskItem extends TaskItem {
  patient: TaskListPatientData;
  taskId: string;
  questionnaireId: string;
  questionnaireScore: number;
  questionnaireResponseId: string;
}

export interface TaskListPatientData {
  id: string;
  nameFirst: string;
  nameLast: string;
  state?: string;
  adminGender?: AdministrativeGender;
  genderIdentity?: GenderIdentity;
  dob?: string;
}
export interface TaskListProviderData {
  id: string;
  name: string;
  role: ProfessionalRole;
}
export interface TaskListPrescriptionData {
  id: string;
  medicationName: string;
  dateWritten?: string;
}

export enum TaskListItemType {
  RX_ERRORS = "rx-errors",
  LAB_RESULTS = "lab-results",
  UNSOLICITED_LABS = "lab-unsolicited",
  LAB_FAILED = "lab-failed",
  LAB_NEEDS_SUBMISSION = "lab-needs-submission",
  QUESTIONNAIRE_RESPONSE_REVIEW = "questionnaire-response-review",
}

export const TASK_TYPE_LABELS: Record<TaskListItemType, string> = {
  [TaskListItemType.RX_ERRORS]: "Prescription Error",
  [TaskListItemType.LAB_RESULTS]: "Lab Results",
  [TaskListItemType.UNSOLICITED_LABS]: "Unsolicited Lab",
  [TaskListItemType.LAB_FAILED]: "Lab Error",
  [TaskListItemType.LAB_NEEDS_SUBMISSION]: "Lab Needs Submission",
  [TaskListItemType.QUESTIONNAIRE_RESPONSE_REVIEW]: "Questionnaire Complete",
};

export const useTaskList = ({ enabled = true }: { enabled?: boolean } = {}) => {
  return useApi<TaskListResponse>({
    queryOptions: {
      enabled,
      queryFn: async () => {
        const response =
          await axiosInstance.get<TaskListResponse>("/clinic/task-list/");

        return response.data;
      },
      staleTime: 0,
      gcTime: 0,
      queryKey: QueryKeys.Provider.TASK_LIST,
    },
  });
};

/**
 * Checks if a given `TaskItem` is of type `ErroredRxTaskItem`.
 * @param {TaskItem} task The task item to check.
 * @returns True if the task is an Rx error task, false otherwise.
 */
export const isRxError = (task: TaskItem): task is ErroredRxTaskItem => {
  return task.itemType === TaskListItemType.RX_ERRORS;
};

/**
 * Checks if a given `TaskItem` is of type `LabResultsTaskItem`.
 * @param {TaskItem} task The task list item to check.
 * @returns True if the task is a lab result, false otherwise.
 */
export const isLabResult = (task: TaskItem): task is LabResultsTaskItem => {
  return task.itemType === TaskListItemType.LAB_RESULTS;
};

/**
 * Checks if a given `TaskItem` is of type `UnsolicitedLabTaskItem`.
 * @param {TaskItem} task The task list item to check.
 * @returns True if the task is an unsolicited lab, false otherwise.
 */
export const isUnsolicitedLab = (
  task: TaskItem,
): task is UnsolicitedLabTaskItem => {
  return task.itemType === TaskListItemType.UNSOLICITED_LABS;
};

/**
 * Checks if a given `TaskItem` is of type `LabFailedTaskItem`.
 * @param {TaskItem} task The task list item to check.
 * @returns True if the task is a lab failed task, false otherwise.
 */
export const isLabFailed = (task: TaskItem): task is LabFailedTaskItem => {
  return task.itemType === TaskListItemType.LAB_FAILED;
};

/**
 * Checks if a given `TaskItem` is of type `LabNeedsSubmissionTaskItem`.
 * @param {TaskItem} task The task list item to check.
 * @returns True if the task is a lab needs submission task, false otherwise.
 */
export const isLabNeedsSubmission = (
  task: TaskItem,
): task is LabNeedsSubmissionTaskItem => {
  return task.itemType === TaskListItemType.LAB_NEEDS_SUBMISSION;
};

/**
 * Checks if a given `TaskItem` is of type `QuestionnaireResponseReviewTaskItem`.
 * @param {TaskItem} task The task list item to check.
 * @returns True if the task is a questionnaire response review task, false otherwise.
 */
export const isQuestionnaireResponseReview = (
  task: TaskItem,
): task is QuestionnaireResponseReviewTaskItem => {
  return task.itemType === TaskListItemType.QUESTIONNAIRE_RESPONSE_REVIEW;
};
